import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Copy, Edit3, Send, Archive, Lock, Save, Trash2,
  RotateCcw, AlertTriangle, ChevronDown, ChevronRight, GripVertical,
} from 'lucide-react';

// =====================================================================
// Construtor de versões do checklist de CONSERVAÇÃO PREDIAL.
// Espelha o construtor da Certificação, com uma diferença de modelo:
//   Certificação: Versão → Sistema (com PESO) → Componente
//   Conservação:  Versão → Área (só nome/ordem) → Item
// A área não tem peso nem flag — todo item vale igual no ICP. Por isso ela é
// coluna no banco, e a tela é a única que a trata como "objeto".
//
// Ciclo: Rascunho → Publicado → Arquivado. Versão já usada por uma inspeção
// vira imutável (o ICP daquela loja foi calculado sobre aqueles itens).
// =====================================================================

const API_BASE = '/api/manutencao/conservacao/versoes';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const jsonAuth = () => ({ ...auth(), 'Content-Type': 'application/json' });

const statusStyle = (s) => {
  if (s === 'Publicado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Rascunho') return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#e2e8f0', color: '#475569' }; // Arquivado
};

const btn = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', background: bg, color, border: 'none',
  borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});

const inputStyle = {
  width: '100%', padding: 9, border: '1px solid #cbd5e1', borderRadius: 6, boxSizing: 'border-box',
};

// ---------------------------------------------------------------------
// Lista de versões
// ---------------------------------------------------------------------
const ConservacaoVersoes = ({ onVoltar }) => {
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null);
  const [mostrarNova, setMostrarNova] = useState(false);
  const [novoNome, setNovoNome] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(API_BASE, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as versões do checklist.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criar = async () => {
    if (!novoNome.trim()) { setErro('Informe o nome da versão.'); return; }
    const r = await fetch(API_BASE, {
      method: 'POST', headers: jsonAuth(), body: JSON.stringify({ nome_versao: novoNome.trim() }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao criar.'); return; }
    setMostrarNova(false); setNovoNome('');
    await carregar();
    setEditando(data.id);
  };

  const duplicar = async (id) => {
    const nome = window.prompt('Nome da nova versão (cópia):');
    if (!nome?.trim()) return;
    const r = await fetch(`${API_BASE}/${id}/duplicar`, {
      method: 'POST', headers: jsonAuth(), body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao duplicar.'); return; }
    await carregar();
    setEditando(data.id);
  };

  const acao = async (id, rota, confirmacao) => {
    if (confirmacao && !window.confirm(confirmacao)) return;
    const r = await fetch(`${API_BASE}/${id}/${rota}`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro na operação.'); return; }
    carregar();
  };

  if (editando) {
    return <VersaoEditor id={editando} onSair={() => { setEditando(null); carregar(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Versões do Checklist de Conservação
        </h3>
      </div>

      <button onClick={() => setMostrarNova((v) => !v)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
        background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8,
        fontWeight: 700, cursor: 'pointer', marginBottom: 16,
      }}>
        <Plus size={16} /> Nova versão (em branco)
      </button>

      {mostrarNova && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
          <input value={novoNome} onChange={(e) => setNovoNome(e.target.value)}
            placeholder="ex.: v2 — Conservação Predial" style={{ ...inputStyle, marginTop: 4 }} />
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button onClick={criar} style={btn('#4f46e5', '#fff')}><Save size={14} /> Criar</button>
            <button onClick={() => setMostrarNova(false)} style={btn('#f1f5f9', '#475569')}>Cancelar</button>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b' }}>
            Para partir de um checklist existente, use <strong>Duplicar</strong> na versão desejada — é
            também o caminho para “editar” uma versão já usada em inspeções.
          </p>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

      {carregando ? <p style={{ color: '#64748b' }}>Carregando…</p> : versoes.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhuma versão ainda. Crie a primeira acima.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {versoes.map((v) => {
            const st = statusStyle(v.STATUS);
            const emUso = Number(v.USOS) > 0;
            const editavel = v.STATUS === 'Rascunho' || (v.STATUS === 'Publicado' && !emUso);
            return (
              <div key={v.ID} style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                      background: st.bg, color: st.color,
                    }}>{v.STATUS}</span>
                    {emUso && (
                      <span title="Versão já usada em inspeções — imutável" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 11, fontWeight: 700, color: '#92400e',
                      }}>
                        <Lock size={12} /> {v.USOS} inspeção(ões)
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#64748b' }}>
                    {v.QTD_ITENS} itens em {v.QTD_AREAS} área(s)
                    {v.DESCRICAO ? ` · ${v.DESCRICAO}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => setEditando(v.ID)} style={btn('#eef2ff', '#4338ca')}>
                    <Edit3 size={14} /> {editavel ? 'Editar' : 'Ver'}
                  </button>
                  <button onClick={() => duplicar(v.ID)} style={btn('#f1f5f9', '#475569')}>
                    <Copy size={14} /> Duplicar
                  </button>
                  {v.STATUS === 'Rascunho' && (
                    <button onClick={() => acao(v.ID, 'publicar')} style={btn('#d1fae5', '#065f46')}>
                      <Send size={14} /> Publicar
                    </button>
                  )}
                  {v.STATUS === 'Publicado' && (
                    <button onClick={() => acao(v.ID, 'arquivar', 'Arquivar esta versão? Ela deixa de aparecer para novas inspeções.')}
                      style={btn('#fef3c7', '#92400e')}>
                      <Archive size={14} /> Arquivar
                    </button>
                  )}
                  {v.STATUS === 'Arquivado' && (
                    <button onClick={() => acao(v.ID, 'republicar')} style={btn('#e0e7ff', '#4338ca')}>
                      <RotateCcw size={14} /> Republicar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Editor de UMA versão: áreas e seus itens.
// ---------------------------------------------------------------------
const VersaoEditor = ({ id, onSair }) => {
  const [versao, setVersao] = useState(null);
  const [areas, setAreas] = useState([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [usos, setUsos] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [recolhidas, setRecolhidas] = useState({}); // área → true (recolhida)

  useEffect(() => {
    (async () => {
      setCarregando(true);
      try {
        const r = await fetch(`${API_BASE}/${id}`, { headers: auth() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        setVersao(data.versao);
        setPodeEditar(data.podeEditar);
        setUsos(data.usos);
        setAreas((data.areas || []).map((a) => ({
          nome: a.area,
          itens: (a.itens || []).map((i) => ({ nome: i.ITEM, permite_na: i.PERMITE_NA !== 0 })),
        })));
      } catch (e) {
        setErro('Erro ao carregar a versão.');
      } finally {
        setCarregando(false);
      }
    })();
  }, [id]);

  // --- edição do rascunho em memória (só grava no Salvar) ---
  const mudarArea = (idx, campo, valor) =>
    setAreas((as) => as.map((a, i) => (i === idx ? { ...a, [campo]: valor } : a)));
  const addArea = () => setAreas((as) => [...as, { nome: '', itens: [] }]);
  const removerArea = (idx) => setAreas((as) => as.filter((_, i) => i !== idx));
  const moverArea = (idx, delta) => setAreas((as) => {
    const alvo = idx + delta;
    if (alvo < 0 || alvo >= as.length) return as;
    const copia = [...as];
    [copia[idx], copia[alvo]] = [copia[alvo], copia[idx]];
    return copia;
  });

  const addItem = (ai) => setAreas((as) => as.map((a, i) =>
    (i === ai ? { ...a, itens: [...a.itens, { nome: '', permite_na: true }] } : a)));
  const mudarItem = (ai, ii, campo, valor) => setAreas((as) => as.map((a, i) =>
    (i === ai ? { ...a, itens: a.itens.map((it, j) => (j === ii ? { ...it, [campo]: valor } : it)) } : a)));
  const removerItem = (ai, ii) => setAreas((as) => as.map((a, i) =>
    (i === ai ? { ...a, itens: a.itens.filter((_, j) => j !== ii) } : a)));

  const salvar = async () => {
    setSalvando(true); setErro(''); setAviso('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT', headers: jsonAuth(),
        body: JSON.stringify({ areas: areas.map((a) => ({ nome: a.nome, itens: a.itens })) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      setAviso('Versão salva.');
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const totalItens = areas.reduce((s, a) => s + a.itens.filter((i) => i.nome.trim()).length, 0);

  if (carregando) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onSair} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {versao?.NOME_VERSAO}
        </h3>
        <span style={{ fontSize: 13, color: '#64748b' }}>{totalItens} itens em {areas.length} área(s)</span>
        {podeEditar && (
          <button onClick={salvar} disabled={salvando} style={{
            ...btn('#4f46e5', '#fff'), marginLeft: 'auto', padding: '10px 16px', fontSize: 14,
            opacity: salvando ? 0.6 : 1,
          }}>
            <Save size={16} /> {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        )}
      </div>

      {!podeEditar && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 16,
          background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, color: '#92400e',
        }}>
          <Lock size={18} />
          <div style={{ fontSize: 13 }}>
            <strong>Somente leitura.</strong> Esta versão já foi usada em {usos} inspeção(ões) — editá-la mudaria
            o ICP de lojas já avaliadas. Para alterar o checklist, <strong>duplique</strong> esta versão.
          </div>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}
      {aviso && <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 16 }}>{aviso}</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {areas.map((a, ai) => {
          const recolhida = recolhidas[ai];
          return (
            <div key={ai} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: 12, flexWrap: 'wrap',
                background: '#f8fafc', borderBottom: recolhida ? 'none' : '1px solid #e2e8f0',
              }}>
                <button onClick={() => setRecolhidas((r) => ({ ...r, [ai]: !r[ai] }))}
                  title={recolhida ? 'Expandir' : 'Recolher'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, lineHeight: 0 }}>
                  {recolhida ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                </button>
                <GripVertical size={16} color="#cbd5e1" />
                <input value={a.nome} disabled={!podeEditar}
                  onChange={(e) => mudarArea(ai, 'nome', e.target.value)}
                  placeholder="Nome da área (ex.: Banheiros)"
                  style={{ ...inputStyle, flex: 1, fontWeight: 800, background: podeEditar ? '#fff' : '#f1f5f9' }} />
                <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {a.itens.length} item(ns)
                </span>
                {podeEditar && (
                  <>
                    <button onClick={() => moverArea(ai, -1)} title="Subir" style={btn('#f1f5f9', '#475569')}>↑</button>
                    <button onClick={() => moverArea(ai, 1)} title="Descer" style={btn('#f1f5f9', '#475569')}>↓</button>
                    <button onClick={() => removerArea(ai)} title="Remover área" style={btn('#fef2f2', '#b91c1c')}>
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>

              {!recolhida && (
                <div style={{ padding: 12 }}>
                  {a.itens.length === 0 && (
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#94a3b8' }}>
                      Nenhum item nesta área.
                      {/* Aviso deliberado: sem itens, a área não sobrevive ao Salvar,
                          porque ela existe no banco através das linhas que a citam. */}
                      {podeEditar && ' Uma área sem itens não é gravada.'}
                    </p>
                  )}
                  <div style={{ display: 'grid', gap: 6 }}>
                    {a.itens.map((it, ii) => (
                      <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <input value={it.nome} disabled={!podeEditar}
                          onChange={(e) => mudarItem(ai, ii, 'nome', e.target.value)}
                          placeholder="Nome do item (ex.: Rejuntes)"
                          style={{ ...inputStyle, flex: 1, minWidth: 160, background: podeEditar ? '#fff' : '#f1f5f9' }} />
                        <label title="Permite marcar 'Não aplicável' quando o item não existe na unidade"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={it.permite_na} disabled={!podeEditar}
                            onChange={(e) => mudarItem(ai, ii, 'permite_na', e.target.checked)} />
                          aceita N/A
                        </label>
                        {podeEditar && (
                          <button onClick={() => removerItem(ai, ii)} title="Remover item" style={btn('#fef2f2', '#b91c1c')}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {podeEditar && (
                    <button onClick={() => addItem(ai)} style={{ ...btn('#eef2ff', '#4338ca'), marginTop: 10 }}>
                      <Plus size={14} /> Adicionar item
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {podeEditar && (
        <button onClick={addArea} style={{
          ...btn('#4f46e5', '#fff'), marginTop: 16, padding: '10px 16px', fontSize: 14,
        }}>
          <Plus size={16} /> Adicionar área
        </button>
      )}

      {podeEditar && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 20, padding: 14,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, color: '#1e40af', fontSize: 13,
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            A ordem das áreas é a <strong>ordem do roteiro físico</strong> da inspeção — o especialista percorre
            o prédio numa sequência (fachada → salão → cozinha → áreas técnicas). Use ↑ ↓ para refletir o caminho real.
          </div>
        </div>
      )}
    </div>
  );
};

export default ConservacaoVersoes;
